import { memo, useCallback, useMemo, useRef, useState } from "react";
import { ActivityIndicator, ScrollView } from "react-native";
import { router, Stack, useLocalSearchParams } from "expo-router";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { View, Text, Button, Card } from "@src/components";
import { type SiteUser } from "@src/features/settings/data/mock-sites";
import { SiteUserList } from "@src/features/settings/components/SiteUserList";
import { Icon } from "@src/components/Icon";
import { showSuccessToast } from "@src/utils/toast";
import { Section } from "@src/features/settings/components/Section";
import { CameraAccessList } from "@src/features/settings/components/CameraAccessList";
import { EditUserBottomSheet } from "@src/features/settings/components/EditUserBottomSheet";
import type { BottomSheetModal } from "@gorhom/bottom-sheet";
import { useAccessManagement } from "@visioni/a3s-access/app";
import { useCameras } from "@src/features/cameras";
import { useCreateRoleAssignment, useDeleteRoleAssignment } from "@visioni/a3s-access/app";
import type { AccessManagementRoleAssignment } from "@visioni/a3s-access/app";
import { buildSiteAccessList } from "@src/features/settings/utils";
import { showErrorToast } from "@src/utils/toast";
import Animated, { FadeIn, FadeOut, LinearTransition } from "react-native-reanimated";
import { isWeb } from "@src/utils/web-helpers";
import { WebLayout } from "@src/components/web/WebLayout.web";

const SiteAccessDetailScreen = memo(() => {
  const { siteId } = useLocalSearchParams<{ siteId: string }>();
  const { theme } = useUnistyles();

  const editUserBottomSheetRef = useRef<BottomSheetModal>(null);
  const [editingUserId, setEditingUserId] = useState<string | null>(null);

  const { data, isLoading: isAccessLoading } = useAccessManagement();
  const { data: cameras, isLoading: isCamerasLoading } = useCameras();
  const isLoading = isAccessLoading || isCamerasLoading;

  const [isUpdatingRole, setIsUpdatingRole] = useState(false);
  const [roleOverrides, setRoleOverrides] = useState<Map<string, SiteUser["role"]>>(new Map());

  // Sites derive from `cameras` even when the access-management call fails —
  // an empty roles shape still surfaces the site so the page can render.
  const sites = useMemo(() => buildSiteAccessList(data ?? { users: [], roles: [] }, cameras), [data, cameras]);
  const site = useMemo(() => sites.find((s) => s.id === siteId), [sites, siteId]);
  const users = useMemo(
    () =>
      (site?.users ?? []).map((u) => {
        const override = roleOverrides.get(u.id);
        return override ? { ...u, role: override } : u;
      }),
    [site?.users, roleOverrides],
  );

  const editingUser = useMemo(() => users.find((u) => u.id === editingUserId), [users, editingUserId]);

  // Users with at least one camera-level permission on a camera that belongs to this site.
  const { cameraAccessUsers, cameraCountByUser } = useMemo<{
    cameraAccessUsers: SiteUser[];
    cameraCountByUser: Map<string, number>;
  }>(() => {
    if (!data || !siteId) return { cameraAccessUsers: [], cameraCountByUser: new Map() };
    const countByUser = new Map<string, number>();
    for (const a of data.cameras) {
      if (!a.camera.startsWith(`${siteId}/`)) continue;
      const uid = String(a.user);
      countByUser.set(uid, (countByUser.get(uid) ?? 0) + 1);
    }
    const userById = new Map(data.users.map((u) => [String(u.id), u]));
    const list: SiteUser[] = [];
    for (const uid of countByUser.keys()) {
      const u = userById.get(uid);
      if (!u) continue;
      list.push({
        id: uid,
        name: [u.givenName, u.familyName].filter(Boolean).join(" ") || u.email,
        email: u.email,
        phoneNumber: u.phoneNumber ?? "",
        // role is required by SiteUser but unused by CameraAccessList rendering.
        role: "viewer",
      });
    }
    return { cameraAccessUsers: list, cameraCountByUser: countByUser };
  }, [data, siteId]);

  const createRoleAssignment = useCreateRoleAssignment();
  const deleteRoleAssignment = useDeleteRoleAssignment();

  const handleEditUserSave = useCallback(
    (data: { email: string }) => {
      if (!editingUserId) return;
      // TODO: integrate with API mutation
      showSuccessToast({ message: "User updated" });
    },
    [editingUserId],
  );

  const handleRoleChange = useCallback(
    async (userId: string, newRole: SiteUser["role"]) => {
      const user = users.find((u) => u.id === userId);
      if (!user || user.role === newRole) return;

      // Optimistic update + loader
      setRoleOverrides((prev) => new Map(prev).set(userId, newRole));
      setTimeout(() => {
        setIsUpdatingRole(true);
      }, 500);
      showSuccessToast({
        message: "Role updated",
        description: `${user.name} is now ${newRole.charAt(0).toUpperCase() + newRole.slice(1)}`,
      });

      const oldAssignment = {
        user: userId,
        role: user.role === "manager" ? `site_manager:${siteId}` : `site_user:${siteId}`,
      } as AccessManagementRoleAssignment;
      const newAssignment = {
        user: userId,
        role: newRole === "manager" ? `site_manager:${siteId}` : `site_user:${siteId}`,
      } as AccessManagementRoleAssignment;

      try {
        await deleteRoleAssignment.mutateAsync(oldAssignment);
        await createRoleAssignment.mutateAsync(newAssignment);
        // Wait for query refetch to settle before hiding loader
        await new Promise((resolve) => setTimeout(resolve, 1500));
      } catch {
        setRoleOverrides((prev) => {
          const next = new Map(prev);
          next.delete(userId);
          return next;
        });
        showErrorToast({ message: "Failed to update role" });
      } finally {
        setIsUpdatingRole(false);
      }
    },
    [users, siteId, deleteRoleAssignment, createRoleAssignment],
  );

  const handleNavigateToAddUsers = useCallback(() => {
    router.push(`/settings/site-access/add-users/${siteId}`);
  }, [siteId]);

  const handleNavigateToCameraAccess = useCallback(
    (userId: string) => {
      router.push({
        pathname: "/settings/site-access/camera-access/[userId]",
        params: { userId, siteId },
      });
    },
    [siteId],
  );

  if (isLoading) {
    if (isWeb) {
      return (
        <>
          <Stack.Screen options={{ headerShown: false }} />
          <WebLayout>
            <View style={styles.centered}>
              <ActivityIndicator />
            </View>
          </WebLayout>
        </>
      );
    }

    return (
      <>
        <Stack.Screen
          options={{
            title: "Site Access",
            headerBackButtonDisplayMode: "minimal",
            headerStyle: { backgroundColor: theme.colors.background },
          }}
        />
        <View style={styles.centered}>
          <ActivityIndicator />
        </View>
      </>
    );
  }

  if (!site) {
    if (isWeb) {
      return (
        <>
          <Stack.Screen options={{ headerShown: false }} />
          <WebLayout>
            <View style={styles.centered}>
              <Icon name="errorOutline" size={theme.spacing["13xl"]} color={theme.colors.textMuted} />
              <Text textStyle="lg/regular" color="textMuted">
                Site not found
              </Text>
            </View>
          </WebLayout>
        </>
      );
    }

    return (
      <>
        <Stack.Screen
          options={{
            title: "Site not found",
            headerBackButtonDisplayMode: "minimal",
            headerStyle: { backgroundColor: theme.colors.background },
          }}
        />
        <View style={styles.centered}>
          <Icon
            name="errorOutline"
            size={theme.spacing["13xl"]}
            color={theme.colors.textMuted}
            style={{ marginBottom: theme.spacing["3xl"] }}
          />
          <Text textStyle="lg/regular" color="textMuted">
            Site not found
          </Text>
        </View>
      </>
    );
  }

  if (isWeb) {
    return (
      <>
        <Stack.Screen options={{ headerShown: false }} />
        <WebLayout>
          <ScrollView style={styles.scrollView} contentContainerStyle={styles.contentContainerStyle}>
            <View gap="3xl" flex={1} flexDirection="row" alignItems="flex-start">
              <Card style={{ flexBasis: "50%" }}>
                <Section title="Site-wide access" subtitle="These users only have access to all cameras on the site.">
                  {isUpdatingRole ?
                    <Animated.View entering={FadeIn} exiting={FadeOut}>
                      <ActivityIndicator style={{ paddingVertical: theme.spacing["5xl"] }} />
                    </Animated.View>
                  : <Animated.View entering={FadeIn} exiting={FadeOut}>
                      <SiteUserList
                        users={users}
                        onRoleChange={handleRoleChange}
                        onPressItem={handleNavigateToCameraAccess}
                      />
                    </Animated.View>
                  }
                  <Animated.View layout={LinearTransition}>
                    <Button
                      size="md"
                      variant="primary"
                      label="Add someone"
                      iconRight={<Icon name="plus" size={theme.spacing["5xl"]} color={theme.colors.white.default} />}
                      onPress={handleNavigateToAddUsers}
                      style={styles.button}
                    />
                  </Animated.View>
                </Section>
              </Card>

              <Card style={{ flexBasis: "50%" }}>
                <Section title="Camera access" subtitle="These users have access to specific cameras on the site.">
                  <CameraAccessList
                    users={cameraAccessUsers}
                    cameraCountByUser={cameraCountByUser}
                    onPress={handleNavigateToCameraAccess}
                  />
                </Section>
              </Card>
            </View>
          </ScrollView>
        </WebLayout>
      </>
    );
  }

  return (
    <>
      <Stack.Screen
        options={{
          title: site?.name ?? "Site Access",
          headerBackButtonDisplayMode: "minimal",
          headerStyle: { backgroundColor: theme.colors.background },
        }}
      />
      <ScrollView style={styles.scrollView} contentContainerStyle={styles.contentContainerStyle}>
        <Section title="Site-wide access" subtitle="These users only have access to all cameras on the site.">
          {isUpdatingRole ?
            <Animated.View entering={FadeIn} exiting={FadeOut}>
              <ActivityIndicator style={{ paddingVertical: theme.spacing["5xl"] }} />
            </Animated.View>
          : <Animated.View entering={FadeIn} exiting={FadeOut}>
              <SiteUserList users={users} onRoleChange={handleRoleChange} onPressItem={handleNavigateToCameraAccess} />
            </Animated.View>
          }
          <Animated.View layout={LinearTransition}>
            <Button
              size="md"
              variant="primary"
              label="Add someone"
              iconRight={<Icon name="plus" size={theme.spacing["5xl"]} color={theme.colors.white.default} />}
              onPress={handleNavigateToAddUsers}
              style={styles.button}
            />
          </Animated.View>
        </Section>

        <Section title="Camera access" subtitle="These users have access to specific cameras on the site.">
          <CameraAccessList
            users={cameraAccessUsers}
            cameraCountByUser={cameraCountByUser}
            onPress={handleNavigateToCameraAccess}
          />
        </Section>
      </ScrollView>
      {editingUser && (
        <EditUserBottomSheet
          bottomSheetRef={editUserBottomSheetRef}
          savedUser={editingUser}
          onSave={handleEditUserSave}
        />
      )}
    </>
  );
});

export default SiteAccessDetailScreen;

const styles = StyleSheet.create((theme) => ({
  scrollView: {
    flex: 1,
  },
  contentContainerStyle: {
    flexGrow: 1,
    padding: theme.spacing["3xl"],
    gap: theme.spacing["6xl"],
    paddingBottom: theme.spacing["12xl"],
  },
  centered: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  bannerContainer: {
    backgroundColor: theme.colors.surface2.background,
    borderColor: theme.colors.borderCard,
  },
  button: {
    marginTop: theme.spacing["3xl"],
  },
}));
